/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FocusEvent, useCallback, useMemo } from 'react';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { FormControl, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { asNumber, guessType } from '@rjsf/utils';

import { ArrowRoundedIcon } from '../../../../../../components/svg-icons/ArrowRoundedIcon';
import { useCommonFormWidgetStyles, useSelectFormWidgetStyles } from '../../../../../../styles/customFormWidgets';
import { CustomWidgetProps } from '../../ParametersTab.types';

const nums = new Set(['number', 'integer']);

const processValue = (schema: any, value: any) => {
  const { type, items } = schema;
  if (value === '') {
    return undefined;
  } else if (type === 'array' && items && nums.has(items.type)) {
    return value.map(asNumber);
  } else if (type === 'boolean') {
    return value === 'true';
  } else if (type === 'number') {
    return asNumber(value);
  }

  if (schema.enum) {
    if (schema.enum.every((x: any) => guessType(x) === 'number')) {
      return asNumber(value);
    } else if (schema.enum.every((x: any) => guessType(x) === 'boolean')) {
      return value === 'true';
    }
  }

  return value;
};

type CustomSelectWidgetProps = CustomWidgetProps & { rawErrors: string[] };

// eslint-disable-next-line
export const CustomSelectWidget = (props: CustomSelectWidgetProps) => {
  const {
    schema,
    id,
    options,
    label,
    required,
    disabled,
    readonly,
    value,
    multiple,
    autofocus,
    onChange,
    onBlur,
    onFocus,
    rawErrors,
    formContext,
  } = props;
  const selectWidgetClasses = useSelectFormWidgetStyles();
  const commonWidgetClasses = useCommonFormWidgetStyles();
  const { enumOptions, enumDisabled } = options;

  const name = useMemo(() => label.split('[')[0], [label]);

  const emptyValue = multiple ? [] : '';

  const handleOnChange = useCallback(
    ({ target: { value } }: SelectChangeEvent<{ name?: string; value: unknown }>) =>
      onChange(processValue(schema, value)),
    [onChange, schema],
  );

  const handleBlurFocus = useCallback(
    (type: 'blur' | 'focus') =>
      ({ target: { value } }: FocusEvent<HTMLInputElement>) => {
        const processedValue = processValue(schema, value);

        type === 'blur' ? onBlur(id, processedValue) : onFocus(id, processedValue);
      },
    [id, onBlur, onFocus, schema],
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (formContext?.handleMenuOpen) {
      formContext.handleMenuOpen({ event, name, value: value || null });
    }
  };

  return (
    <FormControl
      className={selectWidgetClasses.selectControl}
      variant="outlined"
      error={!!rawErrors && !!rawErrors.length}
    >
      <InputLabel htmlFor={label}>{label}</InputLabel>
      <Select
        name={label}
        label={label}
        multiple={typeof multiple === 'undefined' ? false : multiple}
        value={typeof value === 'undefined' ? emptyValue : value}
        required={required}
        IconComponent={ArrowRoundedIcon}
        classes={{
          icon: selectWidgetClasses.formIcon,
        }}
        disabled={disabled || readonly}
        autoFocus={autofocus}
        onChange={handleOnChange}
        onBlur={handleBlurFocus('blur')}
        onFocus={handleBlurFocus('focus')}
        className={selectWidgetClasses.select}
        endAdornment={
          <IconButton
            size="small"
            className={cx(commonWidgetClasses.widgetMenuButton, selectWidgetClasses.widgetMenuButton)}
            aria-controls="menu"
            aria-haspopup="true"
            onClick={handleClick}
          >
            <MoreHorizIcon />
          </IconButton>
        }
      >
        {(enumOptions as any).map(({ value, label }: any, i: number) => {
          const disabled: any = enumDisabled && (enumDisabled as any).indexOf(value) !== -1;
          return (
            <MenuItem key={i} value={value} disabled={disabled}>
              {label}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};
